import React, { useEffect, useState, createContext, useContext } from 'react';
import { UserAccount } from '../lib/types';
import { getUserAccount } from '../lib/api';
import { supabase } from '../lib/supabase';
import { Session, User } from '@supabase/supabase-js';
interface UserContextType {
  user: UserAccount | null;
  supabaseUser: User | null;
  session: Session | null;
  isLoading: boolean;
  error: string | null;
  refreshUserData: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{
    error: any;
  }>;
  signUp: (email: string, password: string) => Promise<{
    error: any;
  }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{
    error: any;
  }>;
}
const defaultUserContext: UserContextType = {
  user: null,
  supabaseUser: null,
  session: null,
  isLoading: false,
  error: null,
  refreshUserData: async () => {},
  signIn: async () => ({
    error: null
  }),
  signUp: async () => ({
    error: null
  }),
  signOut: async () => {},
  resetPassword: async () => ({
    error: null
  })
};
const UserContext = createContext<UserContextType>(defaultUserContext);
export const UserProvider: React.FC<{
  children: React.ReactNode;
}> = ({
  children
}) => {
  const [user, setUser] = useState<UserAccount | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    // Check for active session on load
    supabase.auth.getSession().then(({
      data: {
        session
      }
    }) => {
      setSession(session);
      setSupabaseUser(session?.user ?? null);
      if (session?.user) {
        refreshUserData();
      }
    });
    // Listen for auth changes
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setSupabaseUser(session?.user ?? null);
      if (session?.user) {
        refreshUserData();
      } else {
        setUser(null);
      }
    });
    return () => {
      subscription.unsubscribe();
    };
  }, []);
  const refreshUserData = async () => {
    if (!supabaseUser) return;
    try {
      setIsLoading(true);
      setError(null);
      // First check if profile exists
      const {
        data: profile,
        error: profileError
      } = await supabase.from('profiles').select('*').eq('id', supabaseUser.id).single();
      if (profileError && profileError.code !== 'PGRST116') {
        throw new Error(profileError.message);
      }
      // If no profile exists, create one
      if (!profile) {
        const {
          error: insertError
        } = await supabase.from('profiles').insert({
          id: supabaseUser.id,
          email: supabaseUser.email || '',
          usage_minutes: 0,
          usage_limit: 100,
          is_pro: false
        });
        if (insertError) throw new Error(insertError.message);
        // Fetch the newly created profile
        const {
          data: newProfile,
          error: fetchError
        } = await supabase.from('profiles').select('*').eq('id', supabaseUser.id).single();
        if (fetchError) throw new Error(fetchError.message);
        if (newProfile) {
          setUser({
            email: newProfile.email,
            plan: {
              name: newProfile.is_pro ? 'Pro Plan' : 'Free Plan',
              minutesLimit: newProfile.usage_limit,
              isPro: newProfile.is_pro
            },
            usage: {
              minutesUsed: newProfile.usage_minutes,
              percentage: newProfile.usage_minutes / newProfile.usage_limit * 100
            }
          });
        }
      } else {
        // Use existing profile
        setUser({
          email: profile.email,
          plan: {
            name: profile.is_pro ? 'Pro Plan' : 'Free Plan',
            minutesLimit: profile.usage_limit,
            isPro: profile.is_pro
          },
          usage: {
            minutesUsed: profile.usage_minutes,
            percentage: profile.usage_minutes / profile.usage_limit * 100
          }
        });
      }
    } catch (err) {
      console.error('Error fetching user account data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load account data');
    } finally {
      setIsLoading(false);
    }
  };
  const signIn = async (email: string, password: string) => {
    try {
      const {
        error
      } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      return {
        error
      };
    } catch (error) {
      console.error('Error signing in:', error);
      return {
        error
      };
    }
  };
  const signUp = async (email: string, password: string) => {
    try {
      const {
        error
      } = await supabase.auth.signUp({
        email,
        password
      });
      return {
        error
      };
    } catch (error) {
      console.error('Error signing up:', error);
      return {
        error
      };
    }
  };
  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };
  const resetPassword = async (email: string) => {
    try {
      const {
        error
      } = await supabase.auth.resetPasswordForEmail(email);
      return {
        error
      };
    } catch (error) {
      console.error('Error resetting password:', error);
      return {
        error
      };
    }
  };
  return <UserContext.Provider value={{
    user,
    supabaseUser,
    session,
    isLoading,
    error,
    refreshUserData,
    signIn,
    signUp,
    signOut,
    resetPassword
  }} data-id="element-1616">
      {children}
    </UserContext.Provider>;
};
export const useUser = () => useContext(UserContext);